import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InvestmentsTable from '../components/InvestmentsTable';
import useInvestments from '../hooks/useInvestments';
import InvestmentFormModal from '../components/InvestmentFormModal';
import InvestmentDetailModal from '../components/InvestmentDetailModal';
import PortfolioValuesModal from '../components/PortfolioValuesModal';
import FlashMessage from '../components/FlashMessage';
import SummaryCard from '../components/SummaryCard';
import {
  PageContainer,
  ContentContainer,
  Header,
  AddButton,
  Label,
} from '../styles/ComponentStyles';
import apiService from '../services/apiService';
import {
  summarizeInvestments,
  gainLossColor,
  formatMoney,
  formatPct,
} from '../utils/investmentMath';

const Investments = ({ isSidebarOpen }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLimit, setCurrentPageLimit] = useState(100);
  const { investments, totalItems, totalPages, loading, error } = useInvestments(
    currentPage,
    currentPageLimit,
    refreshKey
  );
  const [flashMessage, setFlashMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [detailInvestment, setDetailInvestment] = useState(null);
  const [showPortfolioValues, setShowPortfolioValues] = useState(false);
  const [serverSummary, setServerSummary] = useState(null);

  // Client-side rollup — also the fallback whenever /investments/summary is
  // unavailable, so the page always renders numbers.
  const localSummary = useMemo(() => summarizeInvestments(investments), [investments]);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const data = await apiService.getInvestmentsSummary();
        if (!cancelled) setServerSummary(data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setServerSummary(null);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const totalContributed =
    serverSummary?.total_contributed != null
      ? serverSummary.total_contributed
      : localSummary.totalContributed;
  const totalCurrentValue =
    serverSummary?.computed_current_value != null
      ? serverSummary.computed_current_value
      : localSummary.totalCurrentValue;
  const totalGainLoss =
    serverSummary?.computed_gain_loss != null
      ? serverSummary.computed_gain_loss
      : localSummary.totalGainLoss;
  const totalGainLossPct = serverSummary
    ? serverSummary.computed_gain_loss_pct
    : localSummary.totalGainLossPct;

  const latestPortfolioValue = serverSummary?.latest_portfolio_value || null;
  const portfolioGainLoss = serverSummary?.portfolio_gain_loss;

  const refreshData = useCallback(() => setRefreshKey((k) => k + 1), []);

  const handleFlashMessage = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  const handlePageChange = (p) => setCurrentPage(p);
  const handlePageLimitChange = (l) => setCurrentPageLimit(l);

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleViewDetail = (inv) => setDetailInvestment(inv);
  const handleCloseDetail = () => setDetailInvestment(null);

  const handleSubmit = async (payload) => {
    try {
      if (editingItem) {
        await apiService.updateInvestment(editingItem._id, payload);
        handleFlashMessage('Investment updated.');
      } else {
        await apiService.createInvestment(payload);
        handleFlashMessage('Investment added.');
      }
      refreshData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Something went wrong';
      handleFlashMessage(`Error: ${msg}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiService.deleteInvestment(id);
      handleFlashMessage(res?.data?.message || res?.message || 'Deleted.');
      refreshData();
    } catch (err) {
      handleFlashMessage(err?.response?.data?.detail || 'Error deleting.');
    }
  };

  return (
    <PageContainer>
      <ContentContainer isSidebarOpen={isSidebarOpen}>
        <Header>
          <h1>Investments</h1>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <AddButton onClick={() => setShowPortfolioValues(true)}>Portfolio Values</AddButton>
            <AddButton onClick={handleAdd}>Add New</AddButton>
          </div>
        </Header>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <SummaryCard title="Total Contributed" value={formatMoney(totalContributed)} />
          <SummaryCard title="Current Value" value={formatMoney(totalCurrentValue)} />
          <SummaryCard
            title="Net Gain / Loss"
            value={
              <span style={{ color: gainLossColor(totalGainLoss) }}>
                {totalGainLoss >= 0 ? '+' : ''}
                {formatMoney(totalGainLoss)}
                {totalGainLossPct != null ? ` (${formatPct(totalGainLossPct)})` : ''}
              </span>
            }
          />
          <SummaryCard
            title="Recorded Portfolio Value"
            value={
              latestPortfolioValue ? (
                <span>
                  {formatMoney(latestPortfolioValue.value)}
                  <div style={{ fontSize: '0.75rem', color: '#c9c9c9', fontWeight: 400 }}>
                    as of {latestPortfolioValue.date}
                  </div>
                  <div
                    style={{
                      fontSize: '0.9rem',
                      color: gainLossColor(portfolioGainLoss),
                      fontWeight: 600,
                    }}
                  >
                    {portfolioGainLoss != null
                      ? `${portfolioGainLoss >= 0 ? '+' : ''}${formatMoney(portfolioGainLoss)}${
                          serverSummary?.portfolio_gain_loss_pct != null
                            ? ` (${formatPct(serverSummary.portfolio_gain_loss_pct)})`
                            : ''
                        }`
                      : '—'}
                  </div>
                </span>
              ) : (
                <span>
                  —
                  <div style={{ fontSize: '0.75rem', color: '#c9c9c9', fontWeight: 400 }}>
                    Record one
                  </div>
                </span>
              )
            }
          />
        </div>

        <div>
          <Label>Total: {totalItems}</Label>
          <Label>Pages: {totalPages}</Label>
        </div>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <InvestmentsTable
          investments={investments}
          loading={loading}
          error={error}
          handleDelete={handleDelete}
          currentPage={currentPage}
          currentPageLimit={currentPageLimit}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageLimitChange={handlePageLimitChange}
          onEdit={handleEdit}
          onViewDetail={handleViewDetail}
        />
      </ContentContainer>

      {showModal && (
        <InvestmentFormModal
          editing={!!editingItem}
          initialData={editingItem}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}

      {detailInvestment && (
        <InvestmentDetailModal
          investment={detailInvestment}
          onClose={handleCloseDetail}
          onRefresh={refreshData}
        />
      )}

      {showPortfolioValues && (
        <PortfolioValuesModal
          onClose={() => setShowPortfolioValues(false)}
          onRefresh={refreshData}
        />
      )}
    </PageContainer>
  );
};

export default Investments;
